import { sql } from 'drizzle-orm';
import {
  date,
  index,
  integer,
  jsonb,
  pgTable,
  smallint,
  text,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { id, substrate, timestamps } from './_shared';

export const experienceSnapshots = pgTable('experience_snapshots', {
  id: id(),
  userId: text('user_id').notNull(),
  contentHash: varchar('content_hash', { length: 64 }).notNull(),
  yamlContent: text('yaml_content').notNull(),
  importSource: varchar('import_source', { length: 32 }),
  ...timestamps,
});

export const roles = pgTable(
  'roles',
  {
    id: id(),
    userId: text('user_id').notNull(),
    company: varchar('company', { length: 200 }).notNull(),
    title: varchar('title', { length: 200 }).notNull(),
    location: varchar('location', { length: 120 }),
    employerDescriptor: text('employer_descriptor'),
    startDate: date('start_date').notNull(),
    endDate: date('end_date'),
    isCurrent: integer('is_current').default(0).notNull(),
    summary: text('summary'),
    sortOrder: integer('sort_order').default(0).notNull(),
    metadata: jsonb('metadata'),
    // W3 remove-an-import: the nullable back-link to the corpus_imports event
    // that seeded this role (a plain uuid soft pointer, mirroring source_ref).
    // NULL for pre-existing rows (unknown import — never a back-filled guess)
    // and for hand-added roles. Lets "undo this import" find its rows cheaply.
    importId: uuid('import_id'),
    ...substrate,
    ...timestamps,
  },
  (t) => ({
    userIdx: index('roles_user_idx').on(t.userId),
    dateIdx: index('roles_date_idx').on(t.startDate),
    importIdx: index('roles_import_idx').on(t.importId),
  }),
);

export const achievements = pgTable(
  'achievements',
  {
    id: id(),
    userId: text('user_id').notNull(),
    roleId: uuid('role_id'),
    // Optional finer-grained link to a sub-engagement (role_events.id). Additive +
    // nullable: an achievement still attaches to its role via roleId exactly as today.
    roleEventId: uuid('role_event_id'),
    headline: varchar('headline', { length: 200 }).notNull(),
    situation: text('situation'),
    task: text('task'),
    action: text('action'),
    result: text('result'),
    learning: text('learning'),
    kpiLabel: varchar('kpi_label', { length: 80 }),
    kpiValue: varchar('kpi_value', { length: 80 }),
    tags: jsonb('tags').$type<string[]>().default(sql`'[]'::jsonb`),
    voiceNoteR2Key: varchar('voice_note_r2_key', { length: 256 }),
    embedding: jsonb('embedding'),
    sortOrder: integer('sort_order').default(0).notNull(),
    // Rewrite-loop soft state (additive, 2026-06-10). 'active' | 'merged'.
    // A combine-refine APPLY updates the survivor and marks the other card
    // 'merged' + mergedInto=survivor — greyed in the UI, never hard-deleted
    // (the product-level no-deletions rule; provenance preserved).
    status: varchar('status', { length: 16 }).default('active').notNull(),
    mergedInto: uuid('merged_into'),
    // Flexible per-card metadata (additive, 2026-06-25): captureMode ('compact'|'star'),
    // the relevance / gray marking (not-relevant-at-this-time, never deleted), and room to
    // grow — data points without a migration (static mechanism, dynamic dataset).
    metadata: jsonb('metadata'),
    // The immediately-prior content (the GHOST) for a join-free inline diff: current above,
    // this below. Full history lives in achievement_versions; this is just the hot copy.
    prevContent: jsonb('prev_content'),
    // AXIS-P2 (docs/design/axis-lens-plan-2026-07-06.md, THE SYNTHESIS v2 P2 row) — the
    // per-achievement axis-affinity tag home. NOT the existing `tags` column above (a
    // different, already-live free-text label field — corpus-proving-ground.md §1.1's own
    // do-not-repurpose finding, empirically confirmed: 256/270 rows carry `tags` in the
    // live DB). A scored vector, AxisAffinity[] (AxisAffinityTagger.ts), one entry per axis
    // this achievement's own text was traced to — jsonb because axes are an open-ended,
    // growing set (mirrors industry_vocab.keywords' + dnaProfiles.threads' own choice), never
    // a per-axis column (would force a migration every time P4 auto-adopts a derived axis).
    // Nullable: absent = not yet tagged (backfill target), never an invented empty array.
    axisAffinities: jsonb('axis_affinities'),
    // W3 remove-an-import: the nullable back-link to the corpus_imports event
    // that seeded this achievement (a plain uuid soft pointer, mirroring
    // source_ref). NULL for pre-existing rows (unknown import) and hand-added
    // cards. Lets "undo this import" find its rows cheaply.
    importId: uuid('import_id'),
    ...substrate,
    ...timestamps,
  },
  (t) => ({
    userIdx: index('achievements_user_idx').on(t.userId),
    roleIdx: index('achievements_role_idx').on(t.roleId),
    importIdx: index('achievements_import_idx').on(t.importId),
  }),
);

// The append-only GHOST history (additive, 2026-06-25). NOTHING is unrecoverable: every
// version of an achievement is kept, never overwritten. V0 = the imported original (or the
// earliest recoverable, backfilled from experience_snapshots / the transcript); Vn = a
// voice-enriched rewrite. The diff "why" is the sourceRef provenance pointer (the facet /
// turn that drove it), traced to real captured input, never generated.
export const achievementVersions = pgTable(
  'achievement_versions',
  {
    id: id(),
    userId: text('user_id').notNull(),
    achievementId: uuid('achievement_id').notNull(),
    version: integer('version').notNull(),
    content: jsonb('content').notNull(),
    source: varchar('source', { length: 24 }).notNull(), // imported|voice|refine-apply|merge|backfill
    sourceRef: jsonb('source_ref'), // provenance pointer (facet/turn/session) — traced, never generated
    ...timestamps,
  },
  (t) => ({
    achIdx: index('achievement_versions_ach_idx').on(t.achievementId),
    userIdx: index('achievement_versions_user_idx').on(t.userId),
  }),
);

export const skills = pgTable('skills', {
  id: id(),
  userId: text('user_id').notNull(),
  name: varchar('name', { length: 80 }).notNull(),
  category: varchar('category', { length: 60 }),
  selfRating: smallint('self_rating'),
  lastUsed: date('last_used'),
  evidenceAchievementIds: jsonb('evidence_achievement_ids').$type<string[]>(),
  // ── Capability model (presence layer, 2026-06-14) — additive, all nullable ──
  // The integrity guard: how the user may HONESTLY claim this capability. The
  // BridgeEngine + PresenceEngine may only restate WITHIN this level, never
  // promote ("architected and shipped X" not "engineered X from scratch").
  claimLevel: varchar('claim_level', { length: 24 }), // architected|led-team|hands-on-built|advised|familiar
  // Recruiter-search variants of `name` (the terms recruiters actually query).
  aliases: jsonb('aliases').$type<string[]>(),
  // Provenance of the capability entry.
  source: varchar('source', { length: 16 }).default('derived'), // derived|bridged|user-added
  ...timestamps,
});

export const education = pgTable('education', {
  id: id(),
  userId: text('user_id').notNull(),
  institution: varchar('institution', { length: 200 }).notNull(),
  credential: varchar('credential', { length: 200 }),
  field: varchar('field', { length: 200 }),
  startDate: date('start_date'),
  endDate: date('end_date'),
  notes: text('notes'),
  sortOrder: integer('sort_order').default(0).notNull(),
  ...timestamps,
});

export const certifications = pgTable('certifications', {
  id: id(),
  userId: text('user_id').notNull(),
  name: varchar('name', { length: 200 }).notNull(),
  issuer: varchar('issuer', { length: 200 }),
  issuedDate: date('issued_date'),
  expiresDate: date('expires_date'),
  credentialId: varchar('credential_id', { length: 200 }),
  url: text('url'),
  ...timestamps,
});

export const projects = pgTable('projects', {
  id: id(),
  userId: text('user_id').notNull(),
  name: varchar('name', { length: 200 }).notNull(),
  description: text('description'),
  url: text('url'),
  tags: jsonb('tags').$type<string[]>(),
  startDate: date('start_date'),
  endDate: date('end_date'),
  ...timestamps,
});
